import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';

import { getOnboardingStatus } from '../services/onboarding-status.service';

/**
 * Global accessor for the aggregated onboarding status payload.
 * Backed by the same TanStack Query cache entry the post-login `beforeLoad`
 * and `AppBootstrap` prefetch populate, so any consumer (sidebar gating,
 * banners, route guards) reads consistent state without re-fetching.
 *
 * Gated on `isAuthenticated` because the endpoint requires a subsidiary
 * header — running it for logged-out users would 401.
 *
 * `refetchOnMount: 'always'` opts a consumer into a fresh fetch on mount (the
 * default 5-min staleTime is too long for a status that gates actions right after
 * an import completes — e.g. Feed Audit's "Select & run").
 */
export function useOnboardingStatus(options?: { refetchOnMount?: boolean | 'always' }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: queryKeys.onboarding.status(),
    queryFn: getOnboardingStatus,
    enabled: isAuthenticated,
    ...(options?.refetchOnMount !== undefined ? { refetchOnMount: options.refetchOnMount } : {}),
  });
}
